import React, { createContext, useContext, useReducer, ReactNode } from "react";
import { User } from "../../core/domain/entities/User";
import { Order } from "../../core/domain/entities/Order";

// State Interface
interface AppState {
  user: User | null;
  orders: Order[];
  warehouses: any[];
  notifications: any[];
  analytics: any;
  settings: any;
  loading: boolean;
  error: string | null;
  activeTab: string;
}

// Action Types
type AppAction =
  | { type: "SET_USER"; payload: User | null }
  | { type: "SET_ORDERS"; payload: Order[] }
  | { type: "SET_WAREHOUSES"; payload: any[] }
  | { type: "SET_NOTIFICATIONS"; payload: any[] }
  | { type: "SET_ANALYTICS"; payload: any }
  | { type: "SET_SETTINGS"; payload: any }
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "SET_ERROR"; payload: string | null }
  | { type: "SET_ACTIVE_TAB"; payload: string }
  | { type: "UPDATE_ORDER"; payload: Order }
  | { type: "UPDATE_NOTIFICATION"; payload: { id: string; isRead: boolean } }
  | { type: "CLEAR_ERROR" };

// Initial State
const initialState: AppState = {
  user: null,
  orders: [],
  warehouses: [],
  notifications: [],
  analytics: null,
  settings: null,
  loading: false,
  error: null,
  activeTab: "dashboard",
};

// Reducer
function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case "SET_USER":
      return { ...state, user: action.payload };
    case "SET_ORDERS":
      return { ...state, orders: action.payload };
    case "SET_WAREHOUSES":
      return { ...state, warehouses: action.payload };
    case "SET_NOTIFICATIONS":
      return { ...state, notifications: action.payload };
    case "SET_ANALYTICS":
      return { ...state, analytics: action.payload };
    case "SET_SETTINGS":
      return { ...state, settings: action.payload };
    case "SET_LOADING":
      return { ...state, loading: action.payload };
    case "SET_ERROR":
      return { ...state, error: action.payload };
    case "SET_ACTIVE_TAB":
      return { ...state, activeTab: action.payload };
    case "UPDATE_ORDER":
      return {
        ...state,
        orders: state.orders.map((order) =>
          order.id === action.payload.id ? action.payload : order
        ),
      };
    case "UPDATE_NOTIFICATION":
      return {
        ...state,
        notifications: state.notifications.map((notification) =>
          notification.id === action.payload.id
            ? { ...notification, isRead: action.payload.isRead }
            : notification
        ),
      };
    case "CLEAR_ERROR":
      return { ...state, error: null };
    default:
      return state;
  }
}

// Context
interface AppContextType {
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

// Provider
interface AppProviderProps {
  children: ReactNode;
}

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  return (
    <AppContext.Provider value={{ state, dispatch }}>
      {children}
    </AppContext.Provider>
  );
};

// Hook
export const useApp = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error("useApp must be used within an AppProvider");
  }
  return context;
};
